import React, { useMemo, useState } from 'react';
import { KappaConfig } from '../../types/model';
import { buildKappaHatFromVectors, buildLambdaPrimeFromKappaHat } from '../../utils/kappaShock';

interface KappaShockEditorProps {
  /** Country list in the same index order as `lam` (unsorted — matches backend indices). */
  countries: string[];
  /** Baseline portfolio-share matrix lam[investor][host], NxN, rows sum to 1. */
  lam: number[][];
  config: KappaConfig;
  onChange: (config: KappaConfig) => void;
}

type Side = 'kappa_S' | 'kappa_D';

const DEFAULT_MULTIPLIER = 1.05;

const KappaShockEditor: React.FC<KappaShockEditorProps> = ({ countries, lam, config, onChange }) => {
  const [pickS, setPickS] = useState('');
  const [pickD, setPickD] = useState('');
  const [setAllValue, setSetAllValue] = useState(DEFAULT_MULTIPLIER);

  const sortedCountries = useMemo(() => [...countries].sort((a, b) => a.localeCompare(b)), [countries]);
  const countryIndex = useMemo(() => new Map(countries.map((c, i) => [c, i])), [countries]);

  const availableS = useMemo(
    () => sortedCountries.filter(c => config.kappa_S[c] === undefined),
    [sortedCountries, config.kappa_S]
  );
  const availableD = useMemo(
    () => sortedCountries.filter(c => config.kappa_D[c] === undefined),
    [sortedCountries, config.kappa_D]
  );

  const updateSide = (side: Side, next: Record<string, number>) => {
    onChange({ ...config, [side]: next });
  };

  const addCountry = (side: Side, country: string) => {
    if (!country) return;
    updateSide(side, { ...config[side], [country]: DEFAULT_MULTIPLIER });
  };

  const removeCountry = (side: Side, country: string) => {
    const next = { ...config[side] };
    delete next[country];
    updateSide(side, next);
  };

  const setMultiplier = (side: Side, country: string, value: number) => {
    if (!(value > 0)) return;
    updateSide(side, { ...config[side], [country]: value });
  };

  const applySetAllS = () => {
    if (!(setAllValue > 0)) return;
    const next: Record<string, number> = {};
    countries.forEach(c => { next[c] = setAllValue; });
    updateSide('kappa_S', next);
  };

  // Live preview: closed-form kappa-shock -> lambda' mapping.
  const preview = useMemo(() => {
    try { return { shares: buildLambdaPrimeFromKappaHat(lam, buildKappaHatFromVectors(countries, config.kappa_S, config.kappa_D), config.eta ?? NaN, config.eta_prime ?? config.eta), error: '' }; }
    catch (e) { return { shares: lam, error: String(e) }; }
  }, [lam, countries, config]);
  const lamPrime = preview.shares;

  const sEditedCountries = useMemo(
    () => sortedCountries.filter(c => config.kappa_S[c] !== undefined),
    [sortedCountries, config.kappa_S]
  );
  const dEditedCountries = useMemo(
    () => sortedCountries.filter(c => config.kappa_D[c] !== undefined),
    [sortedCountries, config.kappa_D]
  );

  const inboundShare = (matrix: number[][], hostIdx: number): number => {
    let total = 0;
    for (let n = 0; n < countries.length; n++) {
      if (n === hostIdx) continue;
      total += matrix[n]?.[hostIdx] ?? 0;
    }
    return total;
  };

  const renderRows = (side: Side) => {
    const entries = Object.entries(config[side]).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) {
      return <div style={styles.hint}>No countries added yet.</div>;
    }
    return (
      <div style={styles.rowList}>
        {entries.map(([country, multiplier]) => (
          <div key={country} style={styles.row}>
            <span style={styles.rowCountry}>{country}</span>
            <input
              type="number"
              min={0.001}
              step={0.01}
              value={multiplier}
              onChange={(e) => setMultiplier(side, country, Number(e.target.value))}
              style={styles.numberInput}
            />
            <span style={styles.rowTimes}>×</span>
            <button
              onClick={() => removeCountry(side, country)}
              className="btn-text btn-sm"
              style={{ color: 'var(--color-danger)' }}
              title="Remove"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.hint}>
        κ_ni = κ<sup>S</sup><sub>n</sub>·κ<sup>D</sup><sub>i</sub>·b_ni. κ<sup>S</sup> shocks a country's
        frictions as investor (its whole row); κ<sup>D</sup> shocks a country's frictions as host (its
        whole column). Domestic costs remain one. Supply the baseline dispersion estimate; a counterfactual value changes dispersion.
      </div>
      <label>Baseline dispersion η <input type="number" min="0.000001" value={config.eta ?? ''} onChange={e => onChange({...config, eta: e.target.value ? Number(e.target.value) : undefined})} /></label>
      <label>Counterfactual dispersion η′ <input type="number" min="0.000001" placeholder="Unchanged" value={config.eta_prime ?? ''} onChange={e => onChange({...config, eta_prime: e.target.value ? Number(e.target.value) : undefined})} /></label>
      {preview.error && <p role="alert">{preview.error}</p>}

      <div style={styles.columns}>
        <div style={styles.column}>
          <div style={styles.columnHeader}>κ<sup>S</sup> — as investor (origin)</div>

          <div style={styles.setAllRow}>
            <span style={styles.setAllLabel}>Set all:</span>
            <input
              type="number"
              min={0.001}
              step={0.01}
              value={setAllValue}
              onChange={(e) => setSetAllValue(Number(e.target.value))}
              style={styles.numberInput}
            />
            <button onClick={applySetAllS} className="btn-outline btn-sm">Apply to every country</button>
          </div>

          <div style={styles.pickerRow}>
            <select value={pickS} onChange={(e) => setPickS(e.target.value)} style={styles.select}>
              <option value="">Add country…</option>
              {availableS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              onClick={() => { addCountry('kappa_S', pickS); setPickS(''); }}
              disabled={!pickS}
              className="btn-outline btn-sm"
            >
              Add
            </button>
          </div>

          {renderRows('kappa_S')}
        </div>

        <div style={styles.column}>
          <div style={styles.columnHeader}>κ<sup>D</sup> — as host (destination)</div>

          <div style={styles.pickerRow}>
            <select value={pickD} onChange={(e) => setPickD(e.target.value)} style={styles.select}>
              <option value="">Add country…</option>
              {availableD.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              onClick={() => { addCountry('kappa_D', pickD); setPickD(''); }}
              disabled={!pickD}
              className="btn-outline btn-sm"
            >
              Add
            </button>
          </div>

          {renderRows('kappa_D')}
        </div>
      </div>

      {!preview.error && (sEditedCountries.length > 0 || dEditedCountries.length > 0) && (
        <div style={styles.previewPanel}>
          <div style={styles.previewTitle}>Live implied effect (closed-form λ' preview)</div>

          {sEditedCountries.map(c => {
            const idx = countryIndex.get(c) ?? -1;
            const base = lam[idx]?.[idx] ?? 0;
            const next = lamPrime[idx]?.[idx] ?? 0;

            return (
              <div key={`s-${c}`} style={styles.previewRow}>
                <strong>{c}</strong> home share λ_nn: {(base * 100).toFixed(3)}% → <strong>{(next * 100).toFixed(3)}%</strong>

              </div>
            );
          })}

          {dEditedCountries.map(c => {
            const idx = countryIndex.get(c) ?? -1;
            const base = inboundShare(lam, idx);
            const next = inboundShare(lamPrime, idx);
            return (
              <div key={`d-${c}`} style={styles.previewRow}>
                <strong>{c}</strong> total inbound share (Σ_n≠i λ_ni): {(base * 100).toFixed(3)}% → <strong>{(next * 100).toFixed(3)}%</strong>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  hint: {
    fontSize: '12px',
    color: 'var(--color-text-subtle)',
    fontStyle: 'italic',
  },
  columns: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  column: {
    flex: '1 1 320px',
    minWidth: '280px',
    padding: '14px',
    backgroundColor: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  columnHeader: {
    fontWeight: 600,
    fontSize: '14px',
  },
  setAllRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--color-border)',
  },
  setAllLabel: {
    fontSize: '12px',
    color: 'var(--color-text-subtle)',
  },
  pickerRow: {
    display: 'flex',
    gap: '8px',
  },
  select: {
    flex: 1,
    padding: '6px 8px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    fontSize: '14px',
    backgroundColor: 'white',
  },
  rowList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '260px',
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 6px',
    backgroundColor: 'white',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '14px',
  },
  rowCountry: {
    flex: 1,
    fontWeight: 500,
  },
  rowTimes: {
    color: 'var(--color-text-subtle)',
  },
  numberInput: {
    width: '70px',
    padding: '4px 6px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    textAlign: 'right',
  },
  previewPanel: {
    padding: '10px 12px',
    backgroundColor: '#fef9c3',
    border: '1px solid #fde68a',
    borderRadius: 'var(--radius-sm)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  previewTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#78350f',
    marginBottom: '2px',
  },
  previewRow: {
    fontSize: '12px',
    color: '#78350f',
  },
  previewSub: {
    fontSize: '12px',
    color: '#92703a',
    fontStyle: 'italic',
  },
};

export default KappaShockEditor;
